/**
 * storage.js - localStorage Utilities
 * Handles persistent storage for conversations and user data
 */

import { logger } from '../logger';

/**
 * Generate a unique ID for conversations
 * @returns {string} Unique ID
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get time-based greeting based on the current hour
 * @returns {string} Appropriate greeting for the time of day
 */
export const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Good night';
};

/**
 * Get title from first message or default
 * Handles both regular chats and canvas-only chats
 * @param {Array} messages - Conversation messages
 * @returns {string} Conversation title
 */
export const getConversationTitle = (messages) => {
  if (!messages || messages.length === 0) return 'New Chat';
  
  // Check for canvas-only conversation (first message is dictionary_canvas)
  const firstMessage = messages[0];
  if (firstMessage && firstMessage.type === 'dictionary_canvas') {
    // Use POI name if available, otherwise show generic title
    return firstMessage.poiName || 'Dictionary Analysis';
  }
  
  // Regular chat - find first user message
  const firstUserMessage = messages.find(m => m.type === 'user');
  if (!firstUserMessage) {
    // No user message found, check if there's a canvas message anywhere
    const canvasMessage = messages.find(m => m.type === 'dictionary_canvas');
    if (canvasMessage) {
      return canvasMessage.poiName || 'Dictionary Analysis';
    }
    return 'New Chat';
  }
  
  let text = firstUserMessage.text || '';
  if (text.startsWith('[Deep Research]')) {
    const queryLine = text
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line && line !== '[Deep Research]' && !line.startsWith('Sources:'));
    if (queryLine) {
      const title = queryLine.replace(/^\[Deep Research\]\s*/i, '').trim();
      return title.length > 36 ? `${title.substring(0, 36)}...` : title || 'Deep Research';
    }
    return 'Deep Research';
  }
  return text.length > 30 ? text.substring(0, 30) + '...' : text || 'New Chat';
};

const conversationScore = (conv) => {
  const messages = conv?.messages || [];
  const created = Date.parse(conv?.createdAt || '') || 0;
  return messages.length * 1000 + created;
};

/**
 * Merge server and local conversation lists (prefer richer / newer copy per id).
 * @param {Array} serverList
 * @param {Array} localList
 * @returns {Array}
 */
export const mergeConversations = (serverList = [], localList = []) => {
  const byId = new Map();

  const upsert = (conv) => {
    if (!conv?.id) return;
    const existing = byId.get(conv.id);
    if (!existing || conversationScore(conv) >= conversationScore(existing)) {
      byId.set(conv.id, conv);
    }
  };

  (serverList || []).forEach(upsert);
  (localList || []).forEach(upsert);

  return Array.from(byId.values()).sort(
    (a, b) => conversationScore(b) - conversationScore(a)
  );
};

/**
 * Get user-specific storage key for conversations
 * @param {string} userId - User's unique ID (Google sub claim)
 * @returns {string} Storage key
 */
export const getUserConversationsKey = (userId) => `chat_conversations_${userId}`;

/**
 * Get user-specific storage key for active conversation
 * @param {string} userId - User's unique ID (Google sub claim)
 * @returns {string} Storage key
 */
export const getUserActiveConversationKey = (userId) => `active_conversation_${userId}`;

/**
 * Load conversations from localStorage for a specific user
 * @param {string} userId - User's unique ID
 * @returns {Array} Conversations array
 */
export const loadConversations = (userId) => {
  if (!userId) return [];
  try {
    const storageKey = getUserConversationsKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      logger.debug('Loaded conversations from storage', { count: parsed.length, userId });
      return parsed;
    }
  } catch (error) {
    logger.error('Failed to load conversations from storage', error);
  }
  return [];
};

/**
 * Save conversations to localStorage for a specific user
 * @param {string} userId - User's unique ID
 * @param {Array} conversations - Conversations to save
 */
export const saveConversations = (userId, conversations) => {
  if (!userId) return;
  try {
    const storageKey = getUserConversationsKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(conversations));
    logger.debug('Saved conversations to storage', { count: conversations.length, userId });
  } catch (error) {
    logger.error('Failed to save conversations to storage', error);
  }
};

/**
 * Load active conversation ID from localStorage for a specific user
 * @param {string} userId - User's unique ID
 * @returns {string|null} Active conversation ID
 */
export const loadActiveConversationId = (userId) => {
  if (!userId) return null;
  try {
    const storageKey = getUserActiveConversationKey(userId);
    return localStorage.getItem(storageKey);
  } catch (error) {
    logger.error('Failed to load active conversation ID', error);
    return null;
  }
};

/**
 * Save active conversation ID to localStorage for a specific user
 * @param {string} userId - User's unique ID
 * @param {string} id - Conversation ID
 */
export const saveActiveConversationId = (userId, id) => {
  if (!userId) return;
  try {
    const storageKey = getUserActiveConversationKey(userId);
    if (id) {
      localStorage.setItem(storageKey, id);
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch (error) {
    logger.error('Failed to save active conversation ID', error);
  }
};

